import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// Copy a routine to one or more other gymnasts in the club. onCopied receives
// the number of copies made; on failure the dialog stays open with the
// selection intact.
const CopyRoutineDialog = ({ routine, gymnastId, onCopied, onCancel }) => {
  const [gymnasts, setGymnasts] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [includeNotes, setIncludeNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/gymnasts')
      .then(res => setGymnasts(
        res.data
          .filter(g => !g.isArchived && g.id !== gymnastId)
          .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
      ))
      .catch(() => setError('Could not load gymnasts'));
  }, [gymnastId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!gymnasts) return [];
    return q ? gymnasts.filter(g => `${g.firstName} ${g.lastName}`.toLowerCase().includes(q)) : gymnasts;
  }, [gymnasts, query]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleCopy = async () => {
    setSaving(true);
    setError(null);
    try {
      // Keep the list order so the request is predictable
      const gymnastIds = gymnasts.filter(g => selected.has(g.id)).map(g => g.id);
      const res = await axios.post(`/api/gymnast-routines/${routine.id}/copy`, { gymnastIds, includeNotes });
      onCopied(res.data.routines.length);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy routine');
      setSaving(false);
    }
  };

  const count = selected.size;

  return (
    <div className="modal-overlay">
      <div className="modal copy-routine">
        <div className="modal-header">
          <h3>Copy "{routine.name}"</h3>
          <button type="button" onClick={onCancel} className="close-button" aria-label="Close">×</button>
        </div>

        <input
          type="search"
          className="form-control"
          placeholder="Search gymnasts…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />

        <div className="copy-routine__list">
          {!gymnasts && !error && <p className="text-muted">Loading gymnasts…</p>}
          {gymnasts && visible.length === 0 && <p className="text-muted">No matching gymnasts</p>}
          {visible.map(g => {
            const name = `${g.firstName} ${g.lastName}`;
            return (
              <label key={g.id} className="copy-routine__option">
                <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggle(g.id)} aria-label={name} />
                <span>{name}</span>
              </label>
            );
          })}
        </div>

        <label className="copy-routine__notes">
          <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} />
          Include notes
        </label>
        <p className="text-muted copy-routine__help">
          Each gymnast gets their own copy, starting as Working on.
        </p>

        {error && <p className="gymnast-routines__error">{error}</p>}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleCopy} disabled={count === 0 || saving}>
            {saving ? 'Copying…' : count === 0 ? 'Copy' : `Copy to ${count} gymnast${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyRoutineDialog;
